import { useState } from "react"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Divider,
  TextField,
} from "@mui/material"
import {
  People as PeopleIcon,
  Work as WorkIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  FiberNew as FiberNewIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import AdminReportGenerator from "./AdminReportGenerator"
import ClientBalanceCard from "../../components/ClientBalanceCard"

function StatCardCompact({ title, value, icon, color = "primary", subtitle }) {
  return (
    <Card sx={{ height: "100%", p: 1.5 }}>
      <CardContent sx={{ p: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default function AdminDashboard() {
  const [dateFilter, setDateFilter] = useState("today")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [reportDialogOpen, setReportDialogOpen] = useState(false)

  // Build query params
  const queryParams = new URLSearchParams()
  if (dateFilter === "custom" && startDate && endDate) {
    queryParams.append("startDate", startDate)
    queryParams.append("endDate", endDate)
  } else {
    queryParams.append("filter", dateFilter)
  }

  const { data: stats, isLoading, error, refetch } = useApiQuery(
    ["admin-stats", dateFilter, startDate, endDate],
    `/admin/stats?${queryParams.toString()}`
  )

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today": return "Today"
      case "30days": return "Last 30 Days"
      case "month": return "This Month"
      case "all": return "All Time"
      case "custom": 
        if (startDate && endDate) {
          return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
        }
        return "Custom Range"
      default: return "Today"
    }
  }

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter)
    // Clear custom dates when switching to predefined filters
    if (newFilter !== "custom") {
      setStartDate("")
      setEndDate("")
    }
  }

  const handleRefresh = () => {
    refetch()
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading dashboard: {error.message}
      </Alert>
    )
  }

  return (
    <Box>

      {/* HEADER */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Admin Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            System overview & analytics
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <IconButton onClick={handleRefresh} color="primary"><RefreshIcon /></IconButton>
          <Button
            variant="contained"
            startIcon={<AssessmentIcon />}
            onClick={() => setReportDialogOpen(true)}
            size="medium"
          >
            Report
          </Button>
        </Stack>
      </Box>

      {/* FILTER BAR */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>Period:</Typography>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time</InputLabel>
              <Select
                value={dateFilter}
                label="Time"
                onChange={(e) => handleDateFilterChange(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {dateFilter !== "custom" && (
              <Typography variant="body2" color="text.secondary">
                Viewing <strong>{getFilterLabel()}</strong>
              </Typography>
            )}
          </Stack>

          {/* Custom Date Range Inputs */}
          {dateFilter === "custom" && (
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 150 }}
              />
              
              {startDate && endDate && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Viewing <strong>{getFilterLabel()}</strong>
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* SECTION: PERIOD OVERVIEW */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {getFilterLabel()} Overview
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCardCompact
            title="New Jobs"
            value={stats?.newJobsToday || 0}
            subtitle={getFilterLabel()}
            color="info"
            icon={<FiberNewIcon fontSize="small" />}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <StatCardCompact
            title="Processing"
            value={stats?.processingJobsToday || 0}
            subtitle={getFilterLabel()}
            color="warning"
            icon={<PlayArrowIcon fontSize="small" />}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <StatCardCompact
            title="Completed"
            value={stats?.completedJobsToday || 0}
            subtitle={getFilterLabel()}
            color="success"
            icon={<CheckCircleIcon fontSize="small" />}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <StatCardCompact
            title="Cancelled"
            value={stats?.cancelledJobsToday || 0}
            subtitle={getFilterLabel()}
            color="error"
            icon={<CancelIcon fontSize="small" />}
          />
        </Grid>
      </Grid>

      {/* FINANCIAL SECTION */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Financial Summary
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <StatCardCompact
            title="Receipts Created"
            value={stats?.receiptsCreatedToday || 0}
            subtitle={`₹${(stats?.totalReceiptsAmount || 0).toLocaleString()}`}
            color="primary"
            icon={<ReceiptIcon fontSize="small" />}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <StatCardCompact
            title="Jobs Settled"
            value={stats?.jobsSettledToday || 0}
            subtitle={`₹${(stats?.totalSettledAmount || 0).toLocaleString()}`}
            color="success"
            icon={<AccountBalanceIcon fontSize="small" />}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <ClientBalanceCard stats={stats}/>
        </Grid>
      </Grid>

      {/* LOWER SECTION: PAYMENT & JOB SUMMARY */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Payment Breakdown
          </Typography>
          <Divider sx={{ my: 1 }} />

          {stats?.receiptsByPaymentMethod &&
          Object.keys(stats.receiptsByPaymentMethod).length > 0 ? (
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Method</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell align="right">Bank Amount</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {Object.entries(stats.receiptsByPaymentMethod).map(([method, data]) =>
                    Object.entries(data.banks || { "N/A": data.total }).map(
                      ([bank, bankAmount], index) => (
                        <TableRow key={`${method}-${bank}`}>
                          {/* Payment Method — only shown for first bank row */}
                          <TableCell>
                            {index === 0 ? (
                              <Chip label={method.toUpperCase()} size="small" />
                            ) : (
                              ""
                            )}
                          </TableCell>

                          {/* Count */}
                          <TableCell align="right">
                            {index === 0 ? data.count : ""}
                          </TableCell>

                          {/* Total amount */}
                          <TableCell align="right">
                            {index === 0 ? `₹${data.total.toLocaleString()}` : ""}
                          </TableCell>

                          {/* Bank */}
                          <TableCell>{bank}</TableCell>

                          {/* Bank Amount */}
                          <TableCell align="right">
                            ₹{bankAmount.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      )
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>
              No receipts for this period.
            </Typography>
          )}
        </Paper>
      </Grid>


      {/* REPORT MODAL */}
      <AdminReportGenerator
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
      />
    </Box>
  )
}